import { useCallback, useEffect, useState } from "react";
import api, { apiError, localApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/format";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { SignIn, SignOut, MagnifyingGlass, PaperPlaneRight, CircleNotch, WifiHigh, Plus, Trash } from "@phosphor-icons/react";
import { format, subDays } from "date-fns";

const inputCls = "rounded-sm border-input bg-background text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";

export default function Asistencia() {
  const [today, setToday] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sumForm, setSumForm] = useState({
    employee_id: "",
    start: format(subDays(new Date(), 13), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd"),
  });
  const [punching, setPunching] = useState(false);
  const [searching, setSearching] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get("/attendance/today"), api.get("/employees")])
      .then(([t, e]) => {
        setToday(Array.isArray(t.data) ? t.data : []);
        setEmployees(Array.isArray(e.data) ? e.data : []);
      })
      .catch((err) => {
        toast.error(apiError(err));
        setToday([]);
      });
  }, []);

  useEffect(load, [load]);

  const punch = async (type) => {
    setPunching(true);
    try {
      await localApi.post("/attendance/punch", { type });
      toast.success(type === "in" ? "Entrada marcada" : "Salida marcada");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPunching(false);
    }
  };

  const fetchSummary = async (e) => {
    e.preventDefault();
    if (!sumForm.employee_id) {
      toast.error("Selecciona un empleado");
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get("/attendance/summary", { params: sumForm });
      setSummary(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSearching(false);
    }
  };

  const fmtH = (iso) => (iso ? new Date(iso).toLocaleTimeString("es-HN", { hour: "2-digit", minute: "2-digit" }) : "—");

  const [repForm, setRepForm] = useState({
    employee_id: "",
    start: format(subDays(new Date(), 13), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd"),
  });
  const [rep, setRep] = useState(null);
  const [repLoading, setRepLoading] = useState(false);
  const [repSending, setRepSending] = useState(false);

  const fetchReport = async (e) => {
    e.preventDefault();
    if (!repForm.employee_id) {
      toast.error("Selecciona un empleado");
      return;
    }
    setRepLoading(true);
    try {
      const { data } = await localApi.get("/fixed-schedule/employee-report", { params: repForm });
      setRep(data);
    } catch (err) {
      toast.error(apiError(err));
      setRep(null);
    } finally {
      setRepLoading(false);
    }
  };

  const sendReport = async () => {
    setRepSending(true);
    try {
      const { data } = await localApi.post("/fixed-schedule/employee-report/send", repForm);
      toast.success(`Reporte enviado a ${data.to}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRepSending(false);
    }
  };

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [wifiCfg, setWifiCfg] = useState(null);
  const [wifiSaving, setWifiSaving] = useState(false);
  const [newIp, setNewIp] = useState("");

  const loadWifi = useCallback(() => {
    localApi.get("/punch-config").then((r) => setWifiCfg(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isAdmin) loadWifi();
  }, [isAdmin, loadWifi]);

  const saveWifi = async (patch) => {
    setWifiSaving(true);
    const next = { ...wifiCfg, ...patch };
    try {
      await localApi.put("/punch-config", { enabled: !!next.enabled, allowed_ips: next.allowed_ips || [], label: next.label || "" });
      setWifiCfg(next);
      toast.success("Restricción de red guardada");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setWifiSaving(false);
    }
  };

  const addMyIp = async () => {
    try {
      const { data } = await localApi.get("/punch-config/my-ip");
      if (data.ip && !(wifiCfg.allowed_ips || []).includes(data.ip)) {
        saveWifi({ allowed_ips: [...(wifiCfg.allowed_ips || []), data.ip] });
      } else {
        toast.info("Esta red ya está en la lista");
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!today) return <Loading label="Cargando asistencia..." />;

  return (
    <div data-testid="asistencia-page">
      <PageHeader title="Asistencia" description="Marcaciones del día y resúmenes por empleado" testid="asistencia-header" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-border bg-card rounded-sm p-5" data-testid="punch-card">
          <h3 className="font-heading text-lg font-bold text-foreground mb-1">Mi marcación</h3>
          <p className="text-xs text-muted-foreground mb-4">Registra tu propia entrada o salida</p>
          <div className="flex gap-2">
            <Button onClick={() => punch("in")} disabled={punching} className="flex-1 rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="punch-in-button">
              <SignIn className="mr-2" size={16} /> Entrada
            </Button>
            <Button onClick={() => punch("out")} disabled={punching} variant="outline" className="flex-1 rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid="punch-out-button">
              <SignOut className="mr-2" size={16} /> Salida
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2 border border-border bg-card rounded-sm p-5" data-testid="summary-card">
          <h3 className="font-heading text-lg font-bold text-foreground mb-4">Resumen por empleado</h3>
          <form onSubmit={fetchSummary} className="flex flex-wrap items-end gap-2" data-testid="summary-form">
            <div className="space-y-1.5 min-w-[200px] flex-1">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Empleado</Label>
              <Select value={sumForm.employee_id} onValueChange={(v) => setSumForm({ ...sumForm, employee_id: v })}>
                <SelectTrigger className={inputCls} data-testid="summary-employee-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Desde</Label>
              <Input type="date" className={inputCls} data-testid="summary-start" value={sumForm.start} onChange={(e) => setSumForm({ ...sumForm, start: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Hasta</Label>
              <Input type="date" className={inputCls} data-testid="summary-end" value={sumForm.end} onChange={(e) => setSumForm({ ...sumForm, end: e.target.value })} />
            </div>
            <Button type="submit" disabled={searching} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="summary-search-button">
              <MagnifyingGlass className="mr-2" size={15} /> Consultar
            </Button>
          </form>
          {summary && (
            <div className="grid grid-cols-3 gap-3 mt-4" data-testid="summary-result">
              {[
                ["Días trabajados", summary.days_worked],
                ["Horas totales", `${Number(summary.total_hours || 0).toFixed(1)}h`],
                ["Horas extra", `${Number(summary.overtime_hours || 0).toFixed(1)}h`],
              ].map(([label, value]) => (
                <div key={label} className="border border-border rounded-sm p-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
                  <p className="font-heading text-2xl font-bold text-foreground mt-1">{value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isAdmin && wifiCfg && (
        <div className="mt-4 border border-border bg-card rounded-sm p-5" data-testid="wifi-config-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-heading text-lg font-bold text-foreground">Restricción por red WiFi</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Si está activa, los empleados solo pueden marcar entrada/salida conectados a la red autorizada (se valida la IP pública de la red).
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <Switch checked={!!wifiCfg.enabled} onCheckedChange={(v) => saveWifi({ enabled: v })} disabled={wifiSaving} data-testid="wifi-enabled-switch" />
              {wifiCfg.enabled ? "Activa" : "Inactiva"}
            </label>
          </div>
          {wifiCfg.enabled && (
            <div className="mt-4 space-y-3">
              <div className="flex flex-wrap gap-2 items-center">
                <Input className={`${inputCls} w-64`} placeholder="IP pública o red (ej. 190.53.10.8)" value={newIp} onChange={(e) => setNewIp(e.target.value)} data-testid="wifi-new-ip" />
                <Button size="sm" variant="outline" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent" disabled={wifiSaving || !newIp.trim()} data-testid="wifi-add-ip"
                  onClick={() => { saveWifi({ allowed_ips: [...(wifiCfg.allowed_ips || []), newIp.trim()] }); setNewIp(""); }}>
                  <Plus className="mr-1.5" size={14} /> Agregar
                </Button>
                <Button size="sm" variant="outline" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent" disabled={wifiSaving} data-testid="wifi-add-current" onClick={addMyIp}>
                  <WifiHigh className="mr-1.5" size={14} /> Agregar esta red
                </Button>
              </div>
              {(wifiCfg.allowed_ips || []).length === 0 ? (
                <p className="text-xs text-amber-500">Sin redes autorizadas — con la restricción activa y la lista vacía nadie podrá marcar.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(wifiCfg.allowed_ips || []).map((ip) => (
                    <span key={ip} className="inline-flex items-center gap-1.5 border border-border rounded-sm px-2.5 py-1 text-xs text-foreground" data-testid={`wifi-ip-${ip}`}>
                      <WifiHigh size={12} className="text-primary" /> {ip}
                      <button onClick={() => saveWifi({ allowed_ips: wifiCfg.allowed_ips.filter((x) => x !== ip) })} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid={`wifi-remove-${ip}`}>
                        <Trash size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 border border-border bg-card rounded-sm p-5" data-testid="punctuality-report-card">
        <h3 className="font-heading text-lg font-bold text-foreground mb-1">Reporte de puntualidad y horas extra</h3>
        <p className="text-xs text-muted-foreground mb-4">Detalle día por día según el horario fijo semanal. Puedes enviarlo directo al correo del empleado.</p>
        <form onSubmit={fetchReport} className="flex flex-wrap items-end gap-2" data-testid="rep-form">
          <div className="space-y-1.5 min-w-[200px] flex-1">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Empleado</Label>
            <Select value={repForm.employee_id} onValueChange={(v) => { setRepForm({ ...repForm, employee_id: v }); setRep(null); }}>
              <SelectTrigger className={inputCls} data-testid="rep-employee-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Desde</Label>
            <Input type="date" className={inputCls} data-testid="rep-start" value={repForm.start} onChange={(e) => setRepForm({ ...repForm, start: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Hasta</Label>
            <Input type="date" className={inputCls} data-testid="rep-end" value={repForm.end} onChange={(e) => setRepForm({ ...repForm, end: e.target.value })} />
          </div>
          <Button type="submit" disabled={repLoading} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="rep-search-button">
            {repLoading ? <CircleNotch className="animate-spin mr-2" size={15} /> : <MagnifyingGlass className="mr-2" size={15} />} Consultar
          </Button>
          {rep && rep.rows.length > 0 && (
            <Button type="button" onClick={sendReport} disabled={repSending} variant="outline"
              className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid="rep-send-button">
              {repSending ? <CircleNotch className="animate-spin mr-2" size={15} /> : <PaperPlaneRight className="mr-2" size={15} />} Enviar al empleado
            </Button>
          )}
        </form>
        {rep && (
          <div className="mt-4" data-testid="rep-result">
            <div className="overflow-x-auto border border-border rounded-sm">
              <table className="w-full text-sm min-w-[640px]" data-testid="rep-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                    <th className="p-3 font-normal">Día</th>
                    <th className="p-3 font-normal">Horario</th>
                    <th className="p-3 font-normal">Marcación</th>
                    <th className="p-3 font-normal">Tardanza</th>
                    <th className="p-3 font-normal">H. extra</th>
                    <th className="p-3 font-normal">Deducción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rep.rows.map((r) => (
                    <tr key={r.date} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`rep-row-${r.date}`}>
                      <td className="p-3 text-foreground/80">{r.weekday} <span className="text-muted-foreground text-xs">{r.date}</span></td>
                      <td className="p-3 text-foreground/80">{r.sched_entry} – {r.sched_exit}</td>
                      <td className="p-3 text-foreground/80">{r.actual_in || "—"} – {r.actual_out || "—"}</td>
                      <td className={`p-3 ${r.late_minutes ? "text-red-400 font-medium" : "text-muted-foreground"}`}>{r.late_minutes ? `${r.late_minutes} min` : "—"}</td>
                      <td className={`p-3 ${r.overtime_hours ? "text-emerald-500 font-medium" : "text-muted-foreground"}`}>{r.overtime_hours ? `${r.overtime_hours} h` : "—"}</td>
                      <td className="p-3 text-foreground/80">{r.late_deduction ? fmtMoney(r.late_deduction) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground mt-3" data-testid="rep-totals">
              Totales: tardanzas <span className="text-foreground font-medium">{rep.totals.late_hours} h</span> · horas extra <span className="text-foreground font-medium">{rep.totals.overtime_hours} h</span> · deducción por tardanzas <span className="text-red-400 font-medium">{fmtMoney(rep.totals.late_deduction)}</span>
            </p>
          </div>
        )}
      </div>

      <div className="mt-4 border border-border bg-card rounded-sm" data-testid="today-table-card">
        <h3 className="font-heading text-lg font-bold text-foreground p-5 pb-4">Marcaciones de hoy</h3>
        {today.length === 0 ? (
          <EmptyState title="Sin turnos hoy" description="No hay empleados con turno programado para hoy." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="today-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-y border-border">
                  <th className="p-3 font-normal">Empleado</th>
                  <th className="p-3 font-normal">Turno</th>
                  <th className="p-3 font-normal">Entrada</th>
                  <th className="p-3 font-normal">Salida</th>
                  <th className="p-3 font-normal">Marcas</th>
                  <th className="p-3 font-normal">Horas</th>
                  <th className="p-3 font-normal">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {today.map((t) => (
                  <tr key={t.employee_id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`today-row-${t.employee_code}`}>
                    <td className="p-3">
                      <p className="text-foreground font-medium">{t.employee_name}</p>
                      <p className="text-xs text-muted-foreground">{t.position || t.employee_code}</p>
                    </td>
                    <td className="p-3 text-foreground/80">{t.shift_name || "—"}</td>
                    <td className="p-3 text-foreground/80">{fmtH(t.first_in)}</td>
                    <td className="p-3 text-foreground/80">{fmtH(t.last_out)}</td>
                    <td className="p-3 text-foreground/80">{t.marks_count ?? 0}</td>
                    <td className="p-3 text-foreground/80">{Number(t.worked_hours || 0).toFixed(1)}h</td>
                    <td className="p-3"><StatusBadge status={t.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
